import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, Search, Ban } from 'lucide-react';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';

const fmtCLP = (n) => {
  const num = Number(n);
  if (Number.isNaN(num)) return '$0';
  return num.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const s = String(iso).slice(0, 10);
    const [y, m, d] = s.split('-');
    if (!y || !m || !d) return s;
    return `${d}-${m}-${y}`;
  } catch { return iso; }
};

export default function ChequesEmitidosPage() {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin';
  const [data, setData] = useState({ items: [], totales: {}, bancos: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroBanco, setFiltroBanco] = useState('todos');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroBanco !== 'todos') params.banco = filtroBanco;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const res = await api.get('/flujo-caja/cxp/cheques-emitidos', { params });
      setData(res.data || { items: [], totales: {}, bancos: [] });
    } catch {
      toast.error('Error cargando cheques emitidos');
    } finally {
      setLoading(false);
    }
  }, [filtroBanco, desde, hasta]);

  useEffect(() => { load(); }, [load]);

  const handleAnular = async (item) => {
    if (!window.confirm(`¿Anular cheque #${item.numero_cheque} de ${item.banco}?\nLa OC ${item.numero_oc} volverá a estado pendiente.`)) return;
    try {
      await api.delete(`/flujo-caja/cxp/cheques-emitidos/${item.id}`);
      toast.success(`Cheque #${item.numero_cheque} anulado`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const filtered = data.items.filter((it) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(it.numero_cheque || '').toLowerCase().includes(q) ||
      (it.nombre_proveedor || '').toLowerCase().includes(q) ||
      String(it.numero_oc || '').includes(q) ||
      (it.banco || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-5" data-testid="cheques-emitidos-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-green-600" />
            Cheques Emitidos
          </h1>
          <p className="text-sm text-muted-foreground">
            Historial de cheques girados. Sirve para saber qué talonarios se usaron y por cuánto.
          </p>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground">Cheques emitidos</div>
            <div className="text-2xl font-bold" data-testid="stat-cant">{data.totales?.cantidad || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground">Monto total</div>
            <div className="text-2xl font-bold text-green-700" data-testid="stat-monto">
              {fmtCLP(data.totales?.monto_total)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground">Bancos usados</div>
            <div className="text-sm mt-1 flex gap-1 flex-wrap">
              {(data.bancos || []).map((b) => (
                <Badge key={b} variant="outline" className="text-xs">{b}</Badge>
              ))}
              {(!data.bancos || data.bancos.length === 0) && <span className="text-xs text-muted-foreground">—</span>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar cheque, proveedor, OC…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="input-search"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Banco</label>
          <Select value={filtroBanco} onValueChange={setFiltroBanco}>
            <SelectTrigger className="w-48" data-testid="filtro-banco">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {(data.bancos || []).map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Desde</label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" data-testid="input-desde" />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Hasta</label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" data-testid="input-hasta" />
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Listado ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No hay cheques emitidos con los filtros actuales.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>N° Cheque</TableHead>
                    <TableHead>Banco</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>OC</TableHead>
                    <TableHead>F. Emisión</TableHead>
                    <TableHead>F. Pago</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Registrado</TableHead>
                    {isAdmin && <TableHead className="text-center">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((it) => (
                    <TableRow key={it.id} data-testid={`row-cheque-emitido-${it.numero_cheque}`}>
                      <TableCell className="font-mono font-semibold text-sm">{it.numero_cheque}</TableCell>
                      <TableCell className="text-sm">{it.banco}</TableCell>
                      <TableCell className="max-w-[240px] truncate text-sm" title={it.nombre_proveedor}>
                        {it.nombre_proveedor}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.numero_oc}</TableCell>
                      <TableCell className="text-xs">{fmtDate(it.fecha_emision)}</TableCell>
                      <TableCell className="text-xs">{fmtDate(it.fecha_pago)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtCLP(it.monto)}</TableCell>
                      <TableCell className="text-[11px] text-muted-foreground">
                        {it.registrado_por?.split('@')[0]}<br />
                        {it.registrado_at ? new Date(it.registrado_at).toLocaleDateString('es-CL') : ''}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleAnular(it)}
                            title="Anular cheque"
                            data-testid={`btn-anular-${it.numero_cheque}`}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
